"use client";

// import { useState } from "react";
import { Product } from "@/types/product.types";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Minus, Plus, Share2 } from "lucide-react";
import { useCart } from "@/features/cart/hook/useCart";



import { stripHtml } from "@/lib/text-utils";
import { getOptionPrice } from "@/lib/pricing";

interface Props {
    product: Product;
    optionQuantities: Record<string, number>;
    setOptionQuantities: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    quantity: number;
    setQuantity: React.Dispatch<React.SetStateAction<number>>;
}

export const ProductInfo = ({
    product,
    optionQuantities,
    setOptionQuantities,
    quantity,
    setQuantity
}: Props) => {
    const { addItem } = useCart();

    const weightAttribute = product?.attributes?.find(
        (attr) => (attr?.attribute?.options ?? []).length > 0
    );

    const weightOptions = weightAttribute?.attribute?.options ?? [];

    const currentPriceDisplay = (() => {
        const effectiveBase = product.salePrice ? Number(product.salePrice) : Number(product.price);

        if (weightOptions.length > 0) {
            const entries = Object.entries(optionQuantities);
            if (entries.length === 0) {
                const prices = weightOptions.map((o) => getOptionPrice(effectiveBase, o));
                const minPrice = Math.min(...prices);
                return `À partir de ${minPrice.toLocaleString()}`;
            } else {
                let total = 0;
                for (const [id, qty] of entries) {
                    const option = weightOptions.find((o) => o.id === id);
                    if (option) {
                        total += getOptionPrice(effectiveBase, option) * qty;
                    }
                }
                return `${total.toLocaleString()}`;
            }
        }

        return `${getOptionPrice(effectiveBase, null).toLocaleString()}`;
    })();

    const handleAddToCart = async () => {
        const entries = Object.entries(optionQuantities);
        if (weightOptions.length > 0 && entries.length === 0) {
            return;
        }
        if (entries.length > 0) {
            for (const [optId, qty] of entries) {
                await addItem(product.id, optId, qty);
            }
        } else {
            await addItem(product.id, '', quantity); // fallback if no options exist, add 1 item
        }
    };

    const shareWhatsApp = () => {
        const url = encodeURIComponent(window.location.href);
        window.open(`https://wa.me/?text=${url}`, "_blank");
    };
    const shareFacebook = () => {
        const url = encodeURIComponent(window.location.href);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
    };

    const descriptionPreview = stripHtml(product.description || product.descriptionHtml).split(".")[0];

    return (
        <div className="space-y-6 bg-primary-50 p-8 rounded-2xl border border-gray-100 shadow-sm">

            <div className="space-y-1">
                {product.sku && (
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">
                        {product.sku}
                    </p>
                )}
                <h1 className="font-brastika text-3xl text-gray-900">
                    {product.name}
                </h1>
                {product.subName && (
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">
                        {product.subName}
                    </p>
                )}



                {descriptionPreview && (
                    <p className="text-gray-500 text-sm italic line-clamp-2">
                        {descriptionPreview}.
                    </p>
                )}
            </div>

            <p className="text-3xl font-brastika text-gray-900">
                {currentPriceDisplay} DA
            </p>

            {weightOptions.length > 0 && (
                <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold text-gray-700">
                        Options:
                    </span>

                    <div className="flex flex-wrap gap-2">
                        {weightOptions.map((option) => {
                            const isSelected = !!optionQuantities[option.id];
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => {
                                        setOptionQuantities(prev => {
                                            const next = { ...prev };
                                            if (next[option.id]) {
                                                delete next[option.id];
                                            } else {
                                                next[option.id] = 1;
                                            }
                                            return next;
                                        });
                                    }}
                                    className={`px-4 py-2 text-sm rounded-lg border transition-all ${isSelected
                                        ? "border-primary-700 bg-primary-700 text-white"
                                        : "border-primary-100 text-primary-900 hover:border-primary-400 bg-primary-50"
                                        }`}
                                >
                                    {option.label} - <span className="text-sm font-semibold">{getOptionPrice(product.salePrice ? Number(product.salePrice) : Number(product.price), option).toLocaleString()} DA</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {/* Qty stepper */}
                <div className="flex items-center gap-2 rounded-sm px-3 py-1.5">
                    <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="text-gray-500 hover:text-gray-900 transition rounded-full border border-gray-400 p-1"
                    >
                        <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">{quantity}</span>
                    <button
                        type="button"
                        onClick={() => setQuantity((q) => q + 1)}
                        className="text-gray-500 hover:text-gray-900 transition rounded-full border border-gray-400 p-1"
                    >
                        <Plus className="h-4 w-4" />
                    </button>
                </div>

                {/* WhatsApp */}
                <button
                    type="button"
                    onClick={shareWhatsApp}
                    className="flex items-center gap-1.5 rounded-sm bg-green-500 hover:bg-green-600 px-4 py-2 text-white text-xs font-bold transition"
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.107 1.523 5.83L0 24l6.336-1.502A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.805 9.805 0 01-5.032-1.387l-.36-.214-3.762.891.952-3.662-.235-.375A9.79 9.79 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
                    </svg>
                    WhatsApp
                </button>

                {/* Facebook */}
                <button
                    type="button"
                    onClick={shareFacebook}
                    className="flex items-center gap-1.5 rounded-sm bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white text-xs font-bold transition"
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                    </svg>
                    Facebook
                </button>

                {/* Generic share */}
                <button
                    type="button"
                    onClick={() => navigator.share?.({ url: window.location.href })}
                    className="flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 h-9 w-9 text-gray-500 transition"
                >
                    <Share2 className="h-4 w-4" />
                </button>
            </div>
            <Button
                onClick={handleAddToCart}
                className="w-full h-10 rounded-md bg-primary-700 hover:bg-primary-800 text-white font-bold text-sm gap-2 tracking-wide"
            >
                <ShoppingCart className="h-5 w-5" />
                AJOUTER AU PANIER
            </Button>
        </div>
    );
};